"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Link, useRouter } from "@/i18n/navigation";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/avatar";
import { SimplePagination } from "@/components/pagination/simple-pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency, formatNumber } from "@/lib/utils/currency";
import { formatDate } from "@/lib/utils/date";
import { useTransactions } from "@/app/[locale]/transactions/_api/use-transaction-queries";
import { components, TransactionStatus } from "@/openapi/generated";
import type { GetTransactionsRequestParameter } from "@/app/[locale]/transactions/_api/transaction-reads";
import { getTransactionStatusBadgeProps } from "@/lib/utils/transaction-status";

const DEFAULT_CURRENCY = 'EUR';
const UNKNOWN_STATUS = 'UNKNOWN';

type TransactionDto = components["schemas"]["TransactionDto"];

interface CompactTransactionsListProps {
  params: Omit<GetTransactionsRequestParameter, "page" | "size">;
  emptyMessage?: string;
  linkToShop?: boolean;
  hideProject?: boolean;
}

export function CompactTransactionsList({ params, emptyMessage, linkToShop, hideProject }: CompactTransactionsListProps) {
  const t = useTranslations("projects");
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;

  const { data, isLoading, error } = useTransactions({ ...params, page: currentPage, size: pageSize });

  const STATUS_LABELS: Record<string, string> = {
    [TransactionStatus.PENDING]: t("compactTransactions.statusPending"),
    [TransactionStatus.VALIDATED]: t("compactTransactions.statusValidated"),
    [TransactionStatus.RECEIVED]: t("compactTransactions.statusReceived"),
    [TransactionStatus.DENIED]: t("compactTransactions.statusDenied"),
    [TransactionStatus.DELETED]: t("compactTransactions.statusDeleted"),
    [TransactionStatus.PAID]: t("compactTransactions.statusPaid"),
  };

  const getStatusBadge = (status: TransactionStatus | string) => {
    const { variant, className } = getTransactionStatusBadgeProps(status as TransactionStatus);
    return (
      <Badge variant={variant} className={`text-xs${className ? ` ${className}` : ""}`}>
        {STATUS_LABELS[status] ?? status}
      </Badge>
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page - 1);
  };

  if (isLoading) {
    return <TransactionsTableSkeleton hideProject={hideProject} />;
  }

  if (error) {
    return (
      <div className="py-8">
        <p className="text-muted-foreground">
          {t("compactTransactions.error")}
        </p>
      </div>
    );
  }

  const transactions = data?.content || [];
  const totalPages = data?.totalPages || 0;

  if (transactions.length === 0) {
    return (
      <div className="py-8">
        <p className="text-muted-foreground">
          {emptyMessage ?? t("compactTransactions.noTransactions")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full">
      {/* Desktop Table (hidden on mobile) */}
      <div className="hidden sm:block border rounded-lg overflow-hidden w-full">
        <div className="overflow-x-auto">
          <table className="w-full min-w-full">
            <thead className="bg-muted/50">
              <tr className="border-b">
                <th className="text-left p-3 text-sm font-medium">{t("compactTransactions.columnUser")}</th>
                <th className="text-left p-3 text-sm font-medium">{t("compactTransactions.columnShop")}</th>
                {!hideProject && (
                  <th className="text-left p-3 text-sm font-medium">{t("compactTransactions.columnProject")}</th>
                )}
                <th className="text-left p-3 text-sm font-medium">{t("compactTransactions.columnStatus")}</th>
                <th className="text-left p-3 text-sm font-medium">{t("compactTransactions.columnAmountDate")}</th>
                <th className="text-left p-3 text-sm font-medium">{t("compactTransactions.columnId")}</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction: TransactionDto, index: number) => {
                const shopHref = linkToShop && transaction.shopSlug ? `/shops/${transaction.shopSlug}` : undefined;
                const shopLogo = transaction.shopLogoUrl ? (
                  <div className="relative w-12 h-12">
                    <Image
                      src={transaction.shopLogoUrl}
                      alt={`${transaction.shopName} Logo`}
                      title={transaction.shopName}
                      fill
                      sizes="48px"
                      className="object-contain rounded"
                    />
                  </div>
                ) : null;
                return (
                <tr
                  key={transaction.id}
                  className={`border-b ${index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}
                >
                  {/* User */}
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <Avatar size="xs" src={transaction.user?.profilePictureUrl} name={transaction.user?.firstName} />
                      <span className="text-sm font-medium truncate max-w-[100px]">
                        {transaction.user?.firstName || t("compactTransactions.anonymous")}
                      </span>
                    </div>
                  </td>

                  {/* Shop */}
                  <td className="p-3">
                    {shopHref ? (
                      <Link href={shopHref} className="hover:opacity-80 transition-opacity">
                        {shopLogo}
                      </Link>
                    ) : shopLogo}
                  </td>

                  {/* Project */}
                  {!hideProject && (
                    <td className="p-3">
                      {transaction.projectLogoUrl ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (transaction.projectSlug) {
                              router.push(`/projects/${transaction.projectSlug}`);
                            }
                          }}
                          className="hover:opacity-80 transition-opacity cursor-pointer"
                          title={transaction.projectName ?? undefined}
                        >
                          <div className="relative w-10 h-10">
                            <Image
                              src={transaction.projectLogoUrl}
                              alt={`${transaction.projectName ?? ""} Logo`}
                              fill
                              sizes="40px"
                              className="object-contain rounded"
                            />
                          </div>
                        </button>
                      ) : null}
                    </td>
                  )}

                  {/* Status */}
                  <td className="p-3">
                    {getStatusBadge(transaction.status || UNKNOWN_STATUS)}
                  </td>

                  {/* Amount & Date */}
                  <td className="p-3">
                    <div className="text-sm">
                      <div className="font-semibold">
                        {formatCurrency(transaction.commissionAmount || 0, transaction.currency || DEFAULT_CURRENCY)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(transaction.transactionTime || "")}
                      </div>
                    </div>
                  </td>

                  {/* Transaction ID */}
                  <td className="p-3">
                    <span className="text-xs font-mono text-muted-foreground">
                      {transaction.id?.slice(0, 8)}
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card Layout (visible on mobile) */}
      <div className="sm:hidden space-y-3">
        {transactions.map((transaction: TransactionDto) => {
          const shopHref = linkToShop && transaction.shopSlug ? `/shops/${transaction.shopSlug}` : undefined;
          const cardClass = `border rounded-lg p-4 bg-background${shopHref ? ' hover:bg-muted/50 transition-colors' : ''}`;

          const cardContent = (
            <>
              {/* Header: User and Status */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Avatar src={transaction.user?.profilePictureUrl} name={transaction.user?.firstName} />
                  <span className="text-sm font-medium">
                    {transaction.user?.firstName || t("compactTransactions.anonymous")}
                  </span>
                </div>
                {getStatusBadge(transaction.status || UNKNOWN_STATUS)}
              </div>

              {/* Shop, Project, and Amount Row */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  {transaction.shopLogoUrl && (
                    <Image
                      src={transaction.shopLogoUrl}
                      alt={`${transaction.shopName} Logo`}
                      title={transaction.shopName}
                      width={32}
                      height={32}
                      className="object-contain rounded"
                    />
                  )}
                  {!hideProject && transaction.projectLogoUrl && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (transaction.projectSlug) {
                          router.push(`/projects/${transaction.projectSlug}`);
                        }
                      }}
                      className="hover:opacity-80 transition-opacity cursor-pointer shrink-0"
                      title={transaction.projectName ?? undefined}
                    >
                      <Image
                        src={transaction.projectLogoUrl}
                        alt={`${transaction.projectName ?? ""} Logo`}
                        width={28}
                        height={28}
                        className="object-contain rounded"
                      />
                    </button>
                  )}
                  <span className="text-sm text-muted-foreground truncate max-w-[120px]">
                    {transaction.shopName || t("compactTransactions.unknownShop")}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold">
                    {formatCurrency(transaction.commissionAmount || 0, transaction.currency || DEFAULT_CURRENCY)}
                  </div>
                </div>
              </div>

              {/* Date and ID Row */}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{formatDate(transaction.transactionTime || "")}</span>
                <span className="font-mono">
                  {t("compactTransactions.idPrefix", { id: transaction.id?.slice(0, 8) })}
                </span>
              </div>
            </>
          );

          return shopHref ? (
            <Link key={transaction.id} href={shopHref} className={`block ${cardClass}`}>
              {cardContent}
            </Link>
          ) : (
            <div key={transaction.id} className={cardClass}>
              {cardContent}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <SimplePagination
            currentPage={currentPage + 1}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Summary */}
      <div className="text-center text-sm text-muted-foreground">
        {data?.totalElements
          ? t("compactTransactions.totalTransactions", { count: formatNumber(data.totalElements) })
          : t("compactTransactions.noTransactionsFound")}
      </div>
    </div>
  );
}

function TransactionsTableSkeleton({ hideProject }: { hideProject?: boolean }) {
  return (
    <div className="space-y-4">
      {/* Desktop skeleton */}
      <div className="hidden sm:block border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr className="border-b">
                <th className="text-left p-3 text-sm font-medium" />
                <th className="text-left p-3 text-sm font-medium" />
                {!hideProject && <th className="text-left p-3 text-sm font-medium" />}
                <th className="text-left p-3 text-sm font-medium" />
                <th className="text-left p-3 text-sm font-medium" />
                <th className="text-left p-3 text-sm font-medium" />
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b">
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <Skeleton className="h-6 w-6 rounded-full" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </td>
                  <td className="p-3">
                    <Skeleton className="h-12 w-12 rounded" />
                  </td>
                  {!hideProject && (
                    <td className="p-3">
                      <Skeleton className="h-10 w-10 rounded" />
                    </td>
                  )}
                  <td className="p-3">
                    <Skeleton className="h-5 w-20" />
                  </td>
                  <td className="p-3">
                    <div className="space-y-1">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </td>
                  <td className="p-3">
                    <Skeleton className="h-3 w-12" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile skeleton */}
      <div className="sm:hidden space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="border rounded-lg p-4 bg-background space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-4 w-20" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Skeleton className="h-8 w-8 rounded" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-4 w-16" />
            </div>
            <div className="flex items-center justify-between">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
